// src/components/layouts/FonctionelTaskToolbar.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, PlusCircle, CalendarDays, ChevronDown, LogOut, Filter, AlertCircle, Clock, Users, Building, Package, X } from 'lucide-react';
import axios from 'axios';
import gecimo_logo from '../../image/gecimo_logo.jpg'; // Assurez-vous que le chemin est correct
import api from '../../api';

const FonctionelTaskToolbar = ({
  onShowForm,
  onSearch,
  onDateStartChange,
  onDateEndChange,
  onPriorityChange = () => { },
  onTypeChange = () => { },
  onModuleChange = () => { },
  onOrganisationChange = () => { },
  onDeadlineTechChange = () => { },
  onDeadlineClientChange = () => { },
  onDateStartTechChange = () => { },
  onDateEndTechChange = () => { },
  onDateDeadlineClientStartChange = () => { },
  onDateDeadlineClientEndChange = () => { },
  onResetFilters = () => { },
  onLogout,
  currentValues = {},
  organisations = [],
  selectedOrg = ''
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const [modules, setModules] = useState([]);
  const [organisationsLocal, setOrganisationsLocal] = useState([]);
  const [showNavMenu, setShowNavMenu] = useState(false);
  const [localSearch, setLocalSearch] = useState(currentValues.searchTerm || '');
  const navigate = useNavigate();
  const [fonctionnelId, setFonctionnelId] = useState(null);


  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userResponse = await api.get('/me', { withCredentials: true });
        if (!userResponse.data.success) {
          console.error('Impossible de récupérer les informations utilisateur.');
          return;
        }

        const user = userResponse.data.user;
        setFonctionnelId(parseInt(user.id, 10));
        console.log('Fonctionnel ID:', fonctionnelId);
      } catch (error) {
        console.error('Erreur lors de la récupération des informations utilisateur.');
      }
    };

    fetchUser();
  }, []);

  const handleNavigation = (path) => {
    navigate(path);
    setShowNavMenu(false); // Ferme le menu après navigation
  };

  useEffect(() => {
    setLocalSearch(currentValues.searchTerm || '');
  }, [currentValues.searchTerm]);

  // Gestion optimisée du changement
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setLocalSearch(value);
    onSearch(value); // Envoi immédiat mais parent peut debouncer
  }, [onSearch]);

  const clearSearch = useCallback(() => {
    setLocalSearch('');
    onSearch('');
  }, [onSearch]);

  // ✅ Chargement des modules et organisations pour les filtres
  useEffect(() => {
    // Charger les modules
    api.get('/modules')
      .then(res => {
        console.log('Modules chargés:', res.data);
        setModules(res.data || []);
      })
      .catch(err => console.error('Erreur chargement modules:', err));

    // Charger les organisations du fonctionnel
    if (fonctionnelId) {
      api.get(`/organisations/fonctionnel/${fonctionnelId}`)
        .then(res => {
          const orgs = res.data.organisations || [];
          setOrganisationsLocal(orgs);
        })
        .catch(err => console.error('Erreur chargement organisations:', err));
    }
  }, [fonctionnelId]);

  // Utiliser les organisations passées en props ou celles chargées localement
  const finalOrganisations = organisations.length > 0 ? organisations : organisationsLocal;

  const priorityOptions = [
    { value: '', label: 'Toutes les priorités' },
    { value: '1', label: 'Basse' },
    { value: '2', label: 'Moyenne' },
    { value: '3', label: 'Haute' },
  ];

  const typeOptions = [
    { value: '', label: 'Tous les types' },
    { value: 'bug', label: 'Bug' },
    { value: 'demande', label: 'Demande' }
  ];

  // ✅ Fonction pour réinitialiser tous les filtres
  const handleResetAllFilters = () => {
    // Réinitialiser les champs du formulaire
    const form = document.querySelector('.filters-form');
    if (form) {
      const inputs = form.querySelectorAll('input, select');
      inputs.forEach(input => {
        if (input.type === 'date' || input.type === 'text') {
          input.value = '';
        } else if (input.tagName === 'SELECT') {
          input.selectedIndex = 0;
        }
      });
    }

    // Réinitialiser la barre de recherche
    const searchInput = document.querySelector('input[placeholder*="Rechercher"]');
    if (searchInput) {
      searchInput.value = '';
    }

    // Appeler la fonction de réinitialisation du parent
    onResetFilters();
  };

  return (
    <div className="sticky top-0 z-20 bg-white border-b border-gray-200 shadow-lg">
      {/* Main Toolbar */}
      <div className="px-4 py-3">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          {/* Logo & Title */}
          <div className="flex items-center space-x-3 min-w-[120px]">
            <div className="relative">
              <img
                src={gecimo_logo}
                alt="Logo"
                className="h-8 w-auto object-contain rounded-md shadow-sm"
              />
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-[#875A7B] rounded-full animate-pulse"></div>
            </div>
            <div className="hidden md:block">
              <h1 className="text-lg font-bold text-gray-800">Tickets</h1>
              <p className="text-xs text-gray-500">Gestion des tâches</p>
            </div>
          </div>

          {/* Organisation Selector - Plus visible */}
          {/* Organisation Selector - Harmonisé avec la navbar */}
          {finalOrganisations && finalOrganisations.length > 0 && (
            <div className="relative min-w-[200px]">
              {/* Badge compteur d'organisations */}
              <div className="absolute -top-1 -right-1 z-10 bg-gradient-to-r from-[#875A7B] to-[#9d6b8f] text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-lg">
                {finalOrganisations.length}
              </div>

              {/* Container principal avec effet glassmorphism */}
              <div className="relative group">
                {/* Fond dégradé animé au survol */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#875A7B]/10 via-[#9d6b8f]/5 to-[#875A7B]/10 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-sm"></div>

                {/* Select principal */}
                <select
                  value={selectedOrg || ''}
                  onChange={e => onOrganisationChange(e.target.value)}
                  className="block w-full rounded-xl border border-gray-200 bg-gradient-to-r from-gray-50 via-white to-gray-50/80 px-4 py-2.5 pr-12 text-sm font-medium text-gray-700 shadow-sm transition-all duration-300 appearance-none cursor-pointer hover:border-[#875A7B]/50 hover:bg-white hover:shadow-lg hover:shadow-[#875A7B]/10 focus:border-[#875A7B] focus:outline-none focus:ring-2 focus:ring-[#875A7B]/20 focus:bg-white focus:shadow-lg group-hover:scale-[1.02] max-h-60 overflow-y-auto"
                  style={{
                    backgroundImage: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(135, 90, 123, 0.02) 50%, rgba(255,255,255,0.9) 100%)'
                  }}
                >
                  <option value="" className="text-gray-500 font-medium py-3">
                    🏢 Tous les clients
                  </option>
                  {finalOrganisations.map(org => (
                    <option
                      key={org.id}
                      value={org.id}
                      className="text-gray-700 font-medium py-3 hover:bg-gradient-to-r hover:from-[#875A7B]/5 hover:to-[#9d6b8f]/5"
                    >
                      🏢 {org.nomOrganisation}
                    </option>
                  ))}
                </select>

                {/* Flèche personnalisée avec animation */}
                <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 flex items-center">
                  <div className="relative">
                    <ChevronDown className="h-4 w-4 text-[#875A7B] transition-all duration-300 group-hover:text-[#9d6b8f] group-hover:scale-110 group-focus-within:rotate-180" />
                    {/* Effet de lueur au survol */}
                    <div className="absolute inset-0 bg-gradient-to-r from-[#875A7B] to-[#9d6b8f] rounded-full blur-sm opacity-0 group-hover:opacity-30 transition-opacity duration-300 -z-10"></div>
                  </div>
                </div>

                {/* Bordure animée */}
                <div className="absolute inset-0 rounded-xl border border-transparent bg-gradient-to-r from-[#875A7B]/20 via-[#9d6b8f]/10 to-[#875A7B]/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></div>

                {/* Indicateur de scroll pour beaucoup d'options */}
                {finalOrganisations.length > 8 && (
                  <div className="absolute bottom-0 right-10 text-xs text-[#875A7B] font-medium opacity-0 group-hover:opacity-70 transition-opacity duration-300 pointer-events-none">
                    ↕️
                  </div>
                )}
              </div>

              {/* Status indicator avec animation */}
              {selectedOrg && (
                <div className="mt-2 flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gradient-to-r from-[#875A7B] to-[#9d6b8f] rounded-full animate-pulse"></div>
                      <div className="w-2 h-2 bg-gradient-to-r from-[#875A7B] to-[#9d6b8f] rounded-full animate-pulse delay-75"></div>
                      <div className="w-2 h-2 bg-gradient-to-r from-[#875A7B] to-[#9d6b8f] rounded-full animate-pulse delay-150"></div>
                    </div>
                    <span className="text-xs font-medium text-[#875A7B] bg-gradient-to-r from-[#875A7B]/10 to-[#9d6b8f]/10 px-2 py-1 rounded-full border border-[#875A7B]/20">
                      Client actif
                    </span>
                  </div>
                </div>
              )}

              {/* Particules décoratives */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none">
                <div className="absolute top-2 right-8 w-1 h-1 bg-[#875A7B] rounded-full animate-ping delay-100"></div>
                <div className="absolute bottom-3 left-6 w-1 h-1 bg-[#9d6b8f] rounded-full animate-pulse delay-300"></div>
                <div className="absolute top-1/2 left-2 w-0.5 h-0.5 bg-[#875A7B]/60 rounded-full animate-bounce delay-500"></div>
              </div>
            </div>
          )}

          {/* Search Bar */}
          {/* <div className="flex-1 max-w-2xl min-w-[200px] mx-4">
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-gray-400 group-focus-within:text-[#875A7B] transition-colors" />
              </div>
              <input
                type="text"
                placeholder="Rechercher par titre, description, module, organisation..."
                defaultValue={currentValues.searchTerm || ''}
                onChange={e => onSearch(e.target.value)}
                className="block w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm bg-gray-50/50 focus:outline-none focus:ring-2 focus:ring-[#875A7B]/20 focus:border-[#875A7B] focus:bg-white transition-all duration-300 hover:border-gray-300"
              />
            </div>
          </div> */}
          <div className="flex-1 max-w-2xl min-w-[200px] mx-4">
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-gray-400 group-focus-within:text-[#875A7B]" />
              </div>
              <input
                type="text"
                placeholder="Rechercher par titre, description, module..."
                value={localSearch}
                onChange={handleSearchChange}
                className="block w-full pl-10 pr-8 py-2 border border-gray-200 rounded-xl text-sm bg-gray-50/50 focus:outline-none focus:ring-2 focus:ring-[#875A7B]/20 focus:border-[#875A7B] transition-all"
              />
              {localSearch && (
                <button
                  onClick={clearSearch}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                </button>
              )}
            </div>
          </div>



          {/* Actions */}
          <div className="flex items-center gap-3">
            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 ${showFilters
                  ? 'bg-[#875A7B] text-white shadow-lg shadow-[#875A7B]/25'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              title="Filtres avancés"
            >
              <Filter className="h-4 w-4" />
              <span className="hidden sm:inline">Filtres</span>
              <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${showFilters ? 'rotate-180' : ''}`} />
            </button>

            {/* Add Button */}
            <button
              onClick={onShowForm}
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[#875A7B] to-[#9d6b8f] text-white rounded-xl text-sm font-medium hover:shadow-lg hover:shadow-[#875A7B]/25 focus:outline-none focus:ring-2 focus:ring-[#875A7B]/20 transition-all duration-300 transform hover:-translate-y-0.5"
              title="Nouveau ticket"
            >
              <PlusCircle className="h-4 w-4" />
              <span className="hidden sm:inline">Nouveau</span>
            </button>

            {/* Logout Button */}
            <button
              onClick={onLogout}
              className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-700 hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-300/20 transition-all duration-300"
              title="Déconnexion"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Déconnexion</span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Date Filters - Visible sur mobile uniquement */}
      <div className="xl:hidden px-4 py-2 border-t border-gray-100 bg-gray-50/50">
        <div className="flex flex-wrap gap-2">
          <div className="flex items-center bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
            <span className="text-xs text-gray-600 mr-2">Début Tech:</span>
            <input
              type="date"
              onChange={e => onDateStartTechChange && onDateStartTechChange(e.target.value)}
              className="text-xs border border-gray-200 rounded px-1 py-1 w-20 bg-white focus:ring-1 focus:ring-[#875A7B]"
            />
          </div>
          <div className="flex items-center bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
            <span className="text-xs text-gray-600 mr-2">Deadline Client:</span>
            <input
              type="date"
              onChange={e => onDateDeadlineClientStartChange && onDateDeadlineClientStartChange(e.target.value)}
              className="text-xs border border-gray-200 rounded px-1 py-1 w-20 bg-white focus:ring-1 focus:ring-[#875A7B]"
            />
          </div>
        </div>
      </div>

      {/* Advanced Filters Panel */}
      {showFilters && (
        <div className="border-t border-gray-200 bg-gradient-to-r from-gray-50 to-white px-4 py-4 animate-in slide-in-from-top-2 duration-200">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 filters-form">
            {/* Date Range Filter */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <CalendarDays className="h-4 w-4 text-[#875A7B]" />
                Période générale
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="date"
                  defaultValue={currentValues.dateStart || ''}
                  onChange={e => onDateStartChange(e.target.value)}
                  className="text-xs border border-gray-200 rounded-lg px-3 py-2 w-full bg-white focus:ring-2 focus:ring-[#875A7B]/20 focus:outline-none focus:border-[#875A7B] transition-all"
                  title="Date de début"
                />
                <span className="text-xs text-gray-400">→</span>
                <input
                  type="date"
                  defaultValue={currentValues.dateEnd || ''}
                  onChange={e => onDateEndChange(e.target.value)}
                  className="text-xs border border-gray-200 rounded-lg px-3 py-2 w-full bg-white focus:ring-2 focus:ring-[#875A7B]/20 focus:outline-none focus:border-[#875A7B] transition-all"
                  title="Date de fin"
                />
              </div>
            </div>

            {/* Priority Filter */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <AlertCircle className="h-4 w-4 text-[#875A7B]" />
                Priorité
              </label>
              <div className="relative">
                <select
                  defaultValue={currentValues.priorityFilter || ''}
                  onChange={e => onPriorityChange(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-[#875A7B]/20 focus:outline-none focus:border-[#875A7B] transition-all appearance-none cursor-pointer"
                >
                  {priorityOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Type Filter */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <Package className="h-4 w-4 text-[#875A7B]" />
                Type
              </label>
              <div className="relative">
                <select
                  defaultValue={currentValues.typeFilter || ''}
                  onChange={e => onTypeChange(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-[#875A7B]/20 focus:outline-none focus:border-[#875A7B] transition-all appearance-none cursor-pointer"
                >
                  {typeOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Module Filter */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <Package className="h-4 w-4 text-[#875A7B]" />
                Module
              </label>
              <div className="relative">
                <select
                  defaultValue={currentValues.moduleFilter || ''}
                  onChange={e => onModuleChange(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-[#875A7B]/20 focus:outline-none focus:border-[#875A7B] transition-all appearance-none cursor-pointer"
                >
                  <option value="">Tous les modules</option>
                  {modules.map(module => (
                    <option key={module.id} value={module.id}>
                      {module.nom}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Organisation Filter - Dans les filtres avancés aussi */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <Building className="h-4 w-4 text-[#875A7B]" />
                Organisation
              </label>
              <div className="relative">
                <select
                  defaultValue={currentValues.organisationFilter || selectedOrg || ''}
                  onChange={e => onOrganisationChange(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-[#875A7B]/20 focus:outline-none focus:border-[#875A7B] transition-all appearance-none cursor-pointer"
                >
                  <option value="">Toutes les organisations</option>
                  {finalOrganisations.map(org => (
                    <option key={org.id} value={org.id}>
                      {org.nomOrganisation}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Technical Deadline Filter */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <Clock className="h-4 w-4 text-[#875A7B]" />
                Échéance technique
              </label>
              <input
                type="date"
                defaultValue={currentValues.deadlineTechFilter || ''}
                onChange={e => onDeadlineTechChange(e.target.value)}
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-[#875A7B]/20 focus:outline-none focus:border-[#875A7B] transition-all"
                title="Échéance technique"
              />
            </div>

            {/* Client Deadline Filter */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <Users className="h-4 w-4 text-[#875A7B]" />
                Échéance client
              </label>
              <input
                type="date"
                defaultValue={currentValues.deadlineClientFilter || ''}
                onChange={e => onDeadlineClientChange(e.target.value)}
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-[#875A7B]/20 focus:outline-none focus:border-[#875A7B] transition-all"
                title="Échéance client"
              />
            </div>
          </div>

          {/* Filter Actions */}
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-200">
            <div className="text-xs text-gray-500">
              Utilisez les filtres pour affiner votre recherche
            </div>
            <button
              onClick={handleResetAllFilters}
              className="text-xs text-[#875A7B] hover:text-[#9d6b8f] font-medium transition-colors hover:underline"
            >
              Réinitialiser tous les filtres
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FonctionelTaskToolbar;